import { Component } from '@theme/component';

/**
 * DAVIDsTEA About us showcase carousel.
 *
 * The card is the scroller, so a finger, a trackpad and the keyboard already work with no
 * script. This adds the two arrows above it and nothing else.
 *
 * Every slide is the full width of the card here, unlike the timeline rail below the numbers
 * band where the six steps all differ, so a move is one viewport rather than a lookup of where
 * each slide would land. Scroll snapping settles the last fraction.
 *
 * Two rules on the arrows, and they are the ones every carousel in the theme follows. The pair
 * only exists when the card overflows what it can show: `data-overflow`, measured on every
 * scroll and every resize rather than counted off the slides. A control that can never move
 * anything is noise, so with a single slide the pair never appears at all. Whenever it is
 * there, both arrows stay in the layout at either end and dim instead of disappearing, so the
 * pair never shifts under the cursor. The previous arrow ships disabled from the Liquid, so the
 * opening state is right before this runs.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} rail
 * @property {HTMLButtonElement} prev
 * @property {HTMLButtonElement} next
 *
 * @extends {Component<Refs>}
 */
export class DtAboutShowcase extends Component {
  requiredRefs = ['rail', 'prev', 'next'];

  connectedCallback() {
    super.connectedCallback();

    const { rail, prev, next } = this.refs;

    prev.addEventListener('click', this.#onPrev);
    next.addEventListener('click', this.#onNext);
    rail.addEventListener('scroll', this.#onScroll, { passive: true });

    this.#observer = new ResizeObserver(this.#sync);
    this.#observer.observe(rail);

    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    const { rail, prev, next } = this.refs;
    prev?.removeEventListener('click', this.#onPrev);
    next?.removeEventListener('click', this.#onNext);
    rail?.removeEventListener('scroll', this.#onScroll);
    this.#observer?.disconnect();
  }

  /** @type {ResizeObserver | undefined} */
  #observer;

  get #behavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /** @param {number} direction */
  #move(direction) {
    const { rail } = this.refs;
    rail.scrollBy({ left: direction * rail.clientWidth, behavior: this.#behavior });
  }

  #onPrev = () => this.#move(-1);
  #onNext = () => this.#move(1);
  #onScroll = () => this.#sync();

  /**
   * A pixel of slack at both ends: browsers land on fractional scroll offsets, and an exact test
   * would leave the arrow live at a stop it cannot move past.
   */
  #sync = () => {
    const { rail, prev, next } = this.refs;
    const max = rail.scrollWidth - rail.clientWidth;

    this.toggleAttribute('data-overflow', max > 1);

    prev.disabled = rail.scrollLeft <= 1;
    next.disabled = max <= 1 || rail.scrollLeft >= max - 1;
  };
}

if (!customElements.get('dt-about-showcase-component')) {
  customElements.define('dt-about-showcase-component', DtAboutShowcase);
}
